/* eslint-disable import/no-cycle */

'use-strict';

import angular, { IHttpService } from 'angular';
import { EExternalAPI } from '../../src/app/@shared-types/api';
import { EStaticFileType } from '../../src/app/@shared-types/misc/file';
import { EConstantType } from '../../src/app/@shared-types/config/enums';
import { TransferCosts } from '../../src/app/@shared-types/vehicle/transfer-costs';
import { ApiService } from '../../src/app/shared/services/api';
import { ToastService } from '../../ui-components/toast/toast.service';
import { FileService } from '../storage/file.service';
import { UsersService } from '../users/users.service';

export interface Constants {
  id: number,
  attribute: EConstantType,
  value: unknown,
  datatype: 'Float' | unknown,
}

export class SettingsAPIService {
  private $http: IHttpService;

  private toastService: ToastService;

  private usersService: UsersService;

  private fileService: FileService;

  private apiService: ApiService;

  constructor(
    $http: IHttpService,
    toastService: ToastService,
    usersService: UsersService,
    fileService: FileService,
    apiService: ApiService,
  ) {
    'ngInject';

    this.$http = $http;
    this.toastService = toastService;
    this.usersService = usersService;
    this.fileService = fileService;
    this.apiService = apiService;
  }

  public getAPIConfig = <T>(api: EExternalAPI): Promise<T> => this.apiService.get<T>(`/api/external-api/config/${api}`);

  public updateAPIConfig = (api: EExternalAPI, config: unknown): Promise<void> => this.apiService.put<void>(`/api/external-api/config/${api}`, config);

  public startLinkEntryImport = (): Promise<void> => this.apiService.get<void>('/api/external-api/link-entry/');

  public getEnabledAPIs = (): Promise<EExternalAPI[]> => this.apiService.get<EExternalAPI[]>('/api/external-api/enabled');

  public getAccessibleAPIs = (): Promise<EExternalAPI[]> => this.apiService.get<EExternalAPI[]>('/api/external-api/accessible');

  /**
   * Returns all saved constants.
   */
  getAllConstants(): Promise<Constants[]> {
    return new Promise((resolve, reject) => {
      this.$http
        .get(`${API_URL}/api/settings/constants`)
        .then((res) => resolve(res.data as Constants[]))
        .catch((err) => {
          this.apiError('Stammdaten konnten nicht abgerufen werden.');
          return reject(err);
        });
    });
  }

  /**
   * Returns all saved transfer costs.
   */
  getAllTransferCosts(): Promise<TransferCosts[]> {
    return new Promise((resolve, reject) => {
      this.$http
        .get(`${API_URL}/api/settings/transfer-costs`)
        .then((res) => resolve(res.data as TransferCosts[]))
        .catch((err) => {
          this.apiError('Überführungskosten konnten nicht abgerufen werden.');
          return reject(err);
        });
    });
  }

  /** Update one or more constants. */
  public updateConstants = (
    constants: { key: EConstantType, value: unknown }[],
  ): Promise<void> => this
    .apiService.put<void>('/api/settings/constants', { constants });

  /**
   * Updates the transferCosts.
   *
   * @param {Array} transferCosts
   */
  public updateTransferCosts = (transferCosts: TransferCosts[]): Promise<void> => this
    .apiService.put<void>('/api/settings/transfer-costs', { transferCosts });

  /**
   * Upload a new profile image as a static to the server.
   * Update the user profile image with the returned static id.
   *
   * @param {Buffer} file
   * @param {number} userId
   * @param {Promise}
   */
  uploadProfileImage(file, userId) {
    return new Promise((resolve, reject) => {
      let newProfileImageId = null;

      this.fileService.apiUploadStatic(file, { type: EStaticFileType.PROFILE_IMAGE, identifier: userId })
        .then((staticId) => {
          newProfileImageId = staticId;
          return this.updateProfileImage(staticId, userId);
        })
        .then(() => {
          const user = this.usersService.getUserFromId(userId);
          user.profile_image = newProfileImageId;
          return resolve(newProfileImageId);
        })
        .catch((e) => {
          this.apiError('Das Profilbild konnte nicht hochgeladen werden.');
          return reject(e);
        });
    });
  }

  /**
   * Update a user's profile image for the supplied user.
   *
   * @param {string} staticId
   * @param {number} userId
   * @returns {Promise}
   */
  updateProfileImage(staticId, userId) {
    return new Promise((resolve, reject) => {
      const postData = {
        staticId,
        userId,
      };

      this.$http
        .post(`${API_URL}/api/users/profile-image`, postData)
        .then(() => resolve())
        .catch((err) => reject(err));
    });
  }

  /**
   * Show an error message and log the error stack to the console
   *
   * @param {string} message
   */
  apiError(message) {
    this.toastService.toast('error', message);
  }
}

export default angular.module('services.settingsApiService', [])
  .service('settingsApiService', SettingsAPIService)
  .name;
